/**
 * Default modules exposed to pre-compiled extensions.
 *
 * These imports are held onto so they aren't optimized away during deploy, and
 * exposed (via the `modules` map) to extensions through the import-map.
 *
 * It also provides the runtime `precompileTemplate` extensions call. Ember's own
 * `@ember/template-compilation#precompileTemplate` throws at runtime (build-time
 * only macro), so we back it with the HOST APP's `precompile`, injected via
 * `setTemplateCompiler`. Using the host's own `precompile` guarantees the wire
 * format matches its glimmer runtime — the addon never bundles a different
 * ember-source.
 */
import _GlimmerComponent from '@glimmer/component';
import * as _tracking from '@glimmer/tracking';
import * as _application from '@ember/application';
import * as _array from '@ember/array';
import * as _EmberComponent from '@ember/component';
import * as _EmberComponentHelper from '@ember/component/helper';
import _TO from '@ember/component/template-only';
import * as _debug from '@ember/debug';
import * as _destroyable from '@ember/destroyable';
import * as _helpers from '@ember/helper';
import * as _modifier from '@ember/modifier';
import * as _object from '@ember/object';
import * as _owner from '@ember/owner';
import * as _runloop from '@ember/runloop';
import * as _template from '@ember/template';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { createTemplateFactory } from '@ember/template-factory';
import * as _utils from '@ember/utils';

import * as _decoratorsRuntime from 'decorator-transforms/runtime';
import { inject as customServiceDecorator } from '../decorators/custom-inject-decorator.ts';

interface PrecompileTemplateOptions {
  scope?: () => Record<string, unknown>;
  strictMode?: boolean;
  moduleName?: string;
}

type PrecompileFn = (
  source: string,
  options?: { strictMode?: boolean; locals?: string[]; moduleName?: string }
) => string;

class DefaultModules {
  // Host-provided `precompile` (hard-private, runtime-enforced).
  #precompile: PrecompileFn | undefined;

  // The module map, built once and cached.
  #modules: Record<string, unknown> | undefined;

  /**
   * Register the host app's runtime template compiler (`precompile`).
   * Must be called before loading any extension that uses `precompileTemplate`.
   */
  setTemplateCompiler(precompile: PrecompileFn): void {
    this.#precompile = precompile;
  }

  /** Whether the host has registered a template compiler. */
  hasTemplateCompiler(): boolean {
    return typeof this.#precompile === 'function';
  }

  /** The modules served to extensions through the import-map. */
  get modules(): Record<string, unknown> {
    return (this.#modules ??= this.#buildModules());
  }

  #buildModules(): Record<string, unknown> {
    return {
      '@ember/application': _application,
      '@ember/array': _array,
      '@ember/component': _EmberComponent,
      '@ember/component/helper': _EmberComponentHelper,
      '@ember/component/template-only': Object.assign(_TO, { default: _TO }),
      '@ember/debug': _debug,
      '@ember/destroyable': _destroyable,
      '@ember/helper': _helpers,
      '@ember/modifier': _modifier,
      '@ember/object': _object,
      '@ember/runloop': _runloop,
      '@ember/service': {
        inject: customServiceDecorator,
        service: customServiceDecorator
      },
      // Plain object (not the class instance) so the loader's Object.keys-based
      // re-export picks up `precompileTemplate`.
      '@ember/template-compilation': {
        precompileTemplate: (
          templateSource: string,
          options?: PrecompileTemplateOptions
        ): unknown => this.#precompileTemplate(templateSource, options)
      },
      '@ember/template-factory': { createTemplateFactory },
      '@ember/utils': _utils,
      '@ember/template': _template,
      '@ember/owner': _owner,

      '@glimmer/component': _GlimmerComponent,
      '@glimmer/tracking': _tracking,
      'decorator-transforms/runtime': _decoratorsRuntime,
      // decorator-transforms ships `runtime-esm` and `runtime` as the SAME file
      // (per its package exports); extensions may be compiled to import either.
      'decorator-transforms/runtime-esm': _decoratorsRuntime
    };
  }

  /**
   * `precompile(...)` returns a string such as:
   *   {"id":"..","block":"..","moduleName":"..","scope":()=>[Bar],"isStrictMode":true}
   * The `scope` value is a JS arrow expression (NOT valid JSON) referencing the
   * lexical bindings by name. Since eval is unavailable, we extract those
   * identifier names (in their emitted order), strip the `scope` key so the
   * remainder is valid JSON, parse it, then re-wire `scope` to the actual values.
   */
  #precompileTemplate(
    templateSource: string,
    options: PrecompileTemplateOptions = {}
  ): unknown {
    if (!this.#precompile) {
      throw new Error(
        'No template compiler registered. The host app must call `registerTemplateCompiler(precompile)` (via ImportMapService) before loading extensions that use `precompileTemplate`.'
      );
    }

    const scopeObj: Record<string, unknown> =
      typeof options.scope === 'function' ? options.scope() : {};

    const locals = Object.keys(scopeObj);
    const strictMode = Boolean(options.strictMode);

    const compiledString = this.#precompile(templateSource, {
      strictMode,
      locals,
      moduleName: options.moduleName
    });

    let scopeNames: string[] = [];
    const jsonString = compiledString.replace(
      /,?"scope":\(\)=>\[([^\]]*)\]/,
      (_match: string, inner: string): string => {
        scopeNames = inner
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean);
        return '';
      }
    );

    const parsed = JSON.parse(jsonString) as Record<string, unknown>;
    parsed['scope'] = (): unknown[] => scopeNames.map((name) => scopeObj[name]);

    if (strictMode) {
      parsed['isStrictMode'] = true;
    }

    return createTemplateFactory(
      parsed as unknown as Parameters<typeof createTemplateFactory>[0]
    );
  }
}

export default new DefaultModules();
